import { useCallback, useEffect, useState } from "react";
import { api, type Task, type TaskDetail } from "../api.ts";
import { captionStyle, colors, sans, serif, shortDate, tokens } from "../theme.ts";
import { BottomSheet } from "./BottomSheet.tsx";
import { EditLine } from "./EditLine.tsx";
import { GlassButton, inputStyle } from "./glass.tsx";
import { TierDot } from "./TaskRow.tsx";

const TIERS: { key: NonNullable<Task["tier"]>; label: string }[] = [
  { key: "must", label: "Must" }, { key: "should", label: "Should" }, { key: "nice", label: "Nice" },
];

/**
 * The task sheet (docs/v2/02-system-design.md §2). Fields edit where they
 * stand and save as they are left: next, why, the date, the tier. Done and
 * Park are the two actions at the bottom; both close the sheet. Parking needs
 * a reason, because the API refuses an empty one. Nothing is deleted.
 *
 * Not built, on purpose: minutes, money and note stay as Home showed them
 * (read-only), the area and value picker, and Dive in (there is no assistant
 * in V2).
 */
export function TaskSheet({
  id, onClose, onChanged, onOpen,
}: {
  id: string; onClose: () => void; onChanged: () => void;
  /** Open another task in this same sheet, from a blocker or an unblocked row. */
  onOpen?: (id: string) => void;
}) {
  const [t, setT] = useState<TaskDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parking, setParking] = useState(false);
  const [because, setBecause] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.task(id).then(setT), [id]);
  useEffect(() => {
    let live = true;
    api.task(id).then((x) => live && setT(x)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [id]);

  /** A field edit: save it, show what the server now holds, tell the screens behind. */
  const edit = async (patch: Record<string, unknown>) => {
    setError(null);
    try { await api.update(id, patch); await load(); onChanged(); }
    catch (e: any) { setError(e?.message ?? "that did not go through"); }
  };

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); onClose(); }
    catch (e: any) { setError(e?.message ?? "that did not go through"); setBusy(false); }
  };

  const meta = t ? [t.mins ? `${t.mins} min` : null, t.money].filter(Boolean) as string[] : [];
  // The task's own why is edited below, so the read-only chain starts after it.
  const chain = t ? (t.why ? t.why_chain.slice(1) : t.why_chain) : [];
  const open = t && t.state !== "done" && t.state !== "parked";

  return (
    <BottomSheet onClose={onClose}>
      {!t && !error && <p style={{ margin: 0, fontSize: 15, color: colors.ink }}>Opening…</p>}
      {error && <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink }}>{error}</p>}
      {t && (
        <div>
          <div style={captionStyle}>{t.breadcrumb}</div>
          <div style={{
            fontFamily: serif, fontSize: tokens.type.sheetTitle.size, lineHeight: tokens.type.sheetTitle.lineHeight,
            margin: "6px 0 10px", color: colors.ink,
          }}>{t.title}</div>

          {t.state === "parked" && (
            <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink2 }}>
              Parked{t.parked_because ? `: ${t.parked_because}` : ""}
            </p>
          )}
          {t.state === "done" && <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink2 }}>Done</p>}
          {t.state === "waiting" && (
            <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink2 }}>
              Waiting{t.follow_up_on ? `, to chase on ${shortDate(t.follow_up_on)}` : ""}
            </p>
          )}

          {meta.length > 0 && <div style={{ fontSize: 13, color: colors.ink2, marginBottom: 10 }}>{meta.join(" · ")}</div>}

          {t.blockers.length > 0 && (
            <Related label="Blocked by" tasks={t.blockers} onOpen={onOpen} />
          )}
          {t.blockers.length === 0 && t.blocked && t.blocked_until && (
            <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink2 }}>Not before {shortDate(t.blocked_until)}</p>
          )}

          {chain.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {chain.map((w, i) => (
                <p key={i} style={{ margin: "0 0 2px", fontSize: 13, lineHeight: 1.5, color: colors.ink2 }}>{w}</p>
              ))}
            </div>
          )}

          {t.value_id !== "MISC" && (
            <EditLine label="Why" value={t.why} placeholder="Add a why (optional)" multiline onSave={(v) => edit({ why: v })} />
          )}

          <EditLine label="Next" value={t.next} placeholder="The one physical action" onSave={(v) => edit({ next: v })} />
          {t.next_now && t.next_now !== t.next && (
            <p style={{ margin: "-6px 0 12px", fontSize: 13, color: colors.ink2 }}>{t.next_now}</p>
          )}

          <div style={{ marginBottom: 12 }}>
            <div style={captionStyle}>When</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
              <input
                type="date" value={t.due ?? ""} aria-label="Date"
                onChange={(e) => edit({ due: e.target.value || null })}
                style={{ ...inputStyle, width: "auto", fontFamily: sans }}
              />
              {t.due && <GlassButton variant="ghost" onClick={() => edit({ due: null })}>No date</GlassButton>}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={captionStyle}>Tier</div>
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              {TIERS.map((x) => {
                const on = t.tier === x.key;
                return (
                  <button
                    key={x.key} aria-pressed={on} onClick={() => !on && edit({ tier: x.key })}
                    style={{
                      display: "inline-flex", alignItems: "center", padding: "7px 14px", borderRadius: 999, cursor: "pointer",
                      fontFamily: sans, fontSize: tokens.type.segment.size, color: on ? colors.ink : colors.ink2,
                      border: `1px solid ${on ? colors.hair2 : colors.hair}`, background: on ? colors.accentSoft : "transparent",
                    }}
                  ><TierDot tier={x.key} />{x.label}</button>
                );
              })}
            </div>
          </div>

          {t.unblocking.length > 0 && <Related label="Unblocks" tasks={t.unblocking} onOpen={onOpen} />}

          {t.note && (
            <div style={{ marginBottom: 12 }}>
              <div style={captionStyle}>Note</div>
              <p style={{ margin: "4px 0 0", fontSize: 14, lineHeight: 1.5, color: colors.ink, whiteSpace: "pre-wrap" }}>{t.note}</p>
            </div>
          )}

          {parking ? (
            <div style={{ marginTop: 16 }}>
              <div style={{ ...captionStyle, marginBottom: 6 }}>Why it is being parked</div>
              <input
                autoFocus value={because} onChange={(e) => setBecause(e.target.value)}
                placeholder="one line" style={{ ...inputStyle, fontFamily: sans }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <GlassButton
                  variant="primary" disabled={!because.trim() || busy}
                  onClick={() => act(() => api.park(t.id, because.trim()))}
                >Park it</GlassButton>
                <GlassButton variant="ghost" onClick={() => setParking(false)}>Back</GlassButton>
              </div>
            </div>
          ) : (
            open && (
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <GlassButton variant="primary" disabled={busy} onClick={() => act(() => api.update(t.id, { state: "done" }))}>Done</GlassButton>
                <GlassButton disabled={busy} onClick={() => setParking(true)}>Park</GlassButton>
              </div>
            )
          )}
        </div>
      )}
    </BottomSheet>
  );
}

/** Tasks this one waits on, or unblocks. Tappable when the sheet can open another. */
function Related({ label, tasks, onOpen }: { label: string; tasks: Task[]; onOpen?: (id: string) => void }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={captionStyle}>{label}</div>
      {tasks.map((x) => (
        <div
          key={x.id} onClick={() => onOpen?.(x.id)} role={onOpen ? "button" : undefined}
          style={{ display: "flex", alignItems: "center", padding: "8px 0", fontSize: 14, color: colors.ink, cursor: onOpen ? "pointer" : "default" }}
        >
          <TierDot tier={x.tier} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.title}</span>
          {x.due && <span style={{ marginLeft: "auto", paddingLeft: 12, fontSize: 13, color: colors.ink2 }}>{shortDate(x.due)}</span>}
        </div>
      ))}
    </div>
  );
}
